/**
 * Supabaseサービス統合エクスポート
 *
 * FL-136 TDDリファクタリングプロジェクトにより、
 * 4,133行のsupabaseService.tsを6つのサービスファイルに分割
 *
 * 分割構成:
 * - SupabaseAuthService: 認証関連機能
 * - SupabaseMasterService: マスターデータ管理
 * - SupabaseCompanyService: 会社管理機能
 * - SupabaseCaseService: 案件管理機能
 * - SupabasePartnerService: 取引先管理機能
 * - SupabaseVehicleService: 車両・陸送員管理機能
 */

// サービスクラスのインポート
import { SupabaseAuthService } from './supabaseAuthService';
import { SupabaseMasterService } from './supabaseMasterService';
import { SupabaseCompanyService } from './supabaseCompanyService';
import { SupabaseCaseService } from './supabaseCaseService';
import { SupabasePartnerService } from './supabasePartnerService';
import { SupabaseVehicleService } from './supabaseVehicleService';
import { SupabaseNotificationService } from './supabaseNotificationService';

// サービスクラスのエクスポート
export { SupabaseAuthService } from './supabaseAuthService';
export { SupabaseMasterService } from './supabaseMasterService';
export { SupabaseCompanyService } from './supabaseCompanyService';
export { SupabaseCaseService } from './supabaseCaseService';
export { SupabasePartnerService } from './supabasePartnerService';
export { SupabaseVehicleService } from './supabaseVehicleService';
export { SupabaseNotificationService } from './supabaseNotificationService';

// 型定義のエクスポート
export type {
  // マスターデータ関連
  Manufacturer,

  // 会社関連
  Company,

  // 案件関連
  Case,
  CompanyCase,

  // 取引先関連
  BusinessPartner,
  CompanyPartnerSelection,

  // 車両関連
  VehicleModel,
  VehicleType,
  LandTransporter,
  CompanyVehicleModelSettings,
} from '@/types';

/**
 * 統合サービスアクセス用のファクトリークラス
 *
 * 使用例:
 * ```typescript
 * import { SupabaseServiceFactory } from '@/services/supabase';
 *
 * const authService = SupabaseServiceFactory.getAuthService();
 * const vehicleService = SupabaseServiceFactory.getVehicleService();
 * ```
 */
export class SupabaseServiceFactory {
  private static authService?: SupabaseAuthService;
  private static masterService?: SupabaseMasterService;
  private static companyService?: SupabaseCompanyService;
  private static caseService?: SupabaseCaseService;
  private static partnerService?: SupabasePartnerService;
  private static vehicleService?: SupabaseVehicleService;
  private static notificationService?: SupabaseNotificationService;

  /**
   * 認証サービスのシングルトンインスタンスを取得
   */
  static getAuthService(): SupabaseAuthService {
    if (!this.authService) {
      this.authService = new SupabaseAuthService();
    }
    return this.authService;
  }

  /**
   * マスターデータサービスのシングルトンインスタンスを取得
   */
  static getMasterService(): SupabaseMasterService {
    if (!this.masterService) {
      this.masterService = new SupabaseMasterService();
    }
    return this.masterService;
  }

  /**
   * 会社管理サービスのシングルトンインスタンスを取得
   */
  static getCompanyService(): SupabaseCompanyService {
    if (!this.companyService) {
      this.companyService = new SupabaseCompanyService();
    }
    return this.companyService;
  }

  /**
   * 案件管理サービスのシングルトンインスタンスを取得
   */
  static getCaseService(): SupabaseCaseService {
    if (!this.caseService) {
      this.caseService = new SupabaseCaseService();
    }
    return this.caseService;
  }

  /**
   * 取引先管理サービスのシングルトンインスタンスを取得
   */
  static getPartnerService(): SupabasePartnerService {
    if (!this.partnerService) {
      this.partnerService = new SupabasePartnerService();
    }
    return this.partnerService;
  }

  /**
   * 車両・陸送員管理サービスのシングルトンインスタンスを取得
   */
  static getVehicleService(): SupabaseVehicleService {
    if (!this.vehicleService) {
      this.vehicleService = new SupabaseVehicleService();
    }
    return this.vehicleService;
  }

  /**
   * 通知サービスのシングルトンインスタンスを取得
   */
  static getNotificationService(): SupabaseNotificationService {
    if (!this.notificationService) {
      const authService = this.getAuthService();
      if (!authService.supabase) {
        throw new Error('Supabase client not initialized');
      }
      this.notificationService = new SupabaseNotificationService(authService.supabase);
    }
    return this.notificationService;
  }

  /**
   * 全サービスのキャッシュをクリア（主にテスト用）
   */
  static clearCache(): void {
    this.authService = undefined;
    this.masterService = undefined;
    this.companyService = undefined;
    this.caseService = undefined;
    this.partnerService = undefined;
    this.vehicleService = undefined;
    this.notificationService = undefined;
  }
}
